import { useEffect, useMemo, useState } from 'react';
import { fetchUiPanelSettings, updateUiPanelLabels, updateUiPanelVisibility } from '../../api';
import { usePanelSettings } from '../../contexts/PanelSettingsContext';
import { normalizeResponse } from '../../contexts/panelSettingsState';
import { useToast } from '../../hooks/use-toast';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import LabelField from './LabelField';
import LayoutTabsCard from './LayoutTabsCard';
import {
  countHiddenPanels,
  countRenamedPanels,
  initialPanelLabels,
  initialPanelVisibility,
  interfacePanelGroups,
  normalizeLabelInput,
  panelFieldKey,
  panelLabelsPayload
} from './interfacePanels';

const describeError = (error) =>
  error?.response?.data?.message || error?.message || 'Неизвестная ошибка';

/** Почему у строки нет галки. Причины разные, и администратору важно знать какая. */
const hideHint = (panel) =>
  panel.isTabLabel
    ? 'составом разделов управляют права учётной записи'
    : 'скрыть нельзя: в карточке живут фильтры раздела';

/**
 * Названия и состав панелей интерфейса.
 *
 * Это настройка показа: данные, запросы и выгрузки не меняются, скрытая панель просто не
 * рисуется. Настройка общая для всей инсталляции, а не персональная, и правит её только админ.
 *
 * Графики, которые строит пакет узлов анализа, сюда не входят — у них свой состав, свои
 * подписи значений и своя карточка на вкладке «Узлы анализа».
 */
const InterfaceTab = () => {
  const { toast } = useToast();
  const panelSettings = usePanelSettings();

  // Локальное оптимистичное состояние. Сохранённое на сервере, введённое в поля и поля,
  // ждущие ответа сервера, — как в карточке графиков пакета нод.
  const [savedLabels, setSavedLabels] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [labelPending, setLabelPending] = useState({});
  const [visibility, setVisibility] = useState(null);
  const [visibilityPending, setVisibilityPending] = useState({});

  const groups = useMemo(() => interfacePanelGroups(), []);

  useEffect(() => {
    let disposed = false;

    const load = async () => {
      try {
        const overrides = normalizeResponse(await fetchUiPanelSettings());
        if (disposed) {
          return;
        }

        const labels = initialPanelLabels(overrides);
        setSavedLabels(labels);
        setDrafts(labels);
        setVisibility(initialPanelVisibility(overrides));
      } catch (error) {
        if (disposed) {
          return;
        }

        // Fail-open: экран открывается с умолчаниями реестра, править их можно сразу.
        const labels = initialPanelLabels({});
        setSavedLabels(labels);
        setDrafts(labels);
        setVisibility(initialPanelVisibility({}));
        toast({
          title: 'Не удалось загрузить настройки интерфейса',
          description: describeError(error),
          variant: 'destructive'
        });
      }
    };

    load();
    return () => {
      disposed = true;
    };
  }, [toast]);

  const setDraft = (key, value) => setDrafts((current) => ({ ...current, [key]: value }));

  const handleToggle = async (panel, next) => {
    setVisibility((current) => ({ ...current, [panel.id]: next }));
    setVisibilityPending((current) => ({ ...current, [panel.id]: true }));

    try {
      // Ответ содержит настройки тенанта целиком, поэтому дашборду хватает его — второго
      // запроса не будет, а новое состояние видно сразу по возврате на вкладку.
      panelSettings.refresh(await updateUiPanelVisibility(panel.id, next));
    } catch (error) {
      setVisibility((current) => ({ ...current, [panel.id]: !next }));
      toast({
        title: 'Не удалось сохранить состав панелей',
        description: describeError(error),
        variant: 'destructive'
      });
    } finally {
      setVisibilityPending((current) => ({ ...current, [panel.id]: false }));
    }
  };

  /**
   * Сохраняет подписи одной панели. Запрос уходит, только если значение действительно
   * изменилось: уход фокуса с нетронутого поля не должен дёргать сервер.
   */
  const saveLabels = async (panel, nextDrafts) => {
    const titleKey = panelFieldKey.title(panel.id);
    const descriptionKey = panelFieldKey.description(panel.id);
    const payload = panelLabelsPayload(panel, nextDrafts);

    const unchanged =
      payload.title === normalizeLabelInput(savedLabels?.[titleKey])
      && payload.description === normalizeLabelInput(savedLabels?.[descriptionKey]);

    if (unchanged) {
      setDrafts((current) => ({
        ...current,
        [titleKey]: savedLabels?.[titleKey] ?? '',
        [descriptionKey]: savedLabels?.[descriptionKey] ?? ''
      }));
      return;
    }

    setLabelPending((current) => ({ ...current, [titleKey]: true, [descriptionKey]: true }));

    try {
      const state = await updateUiPanelLabels(panel.id, payload.title, payload.description);
      setSavedLabels((current) => ({
        ...current,
        [titleKey]: payload.title ?? '',
        [descriptionKey]: payload.description ?? ''
      }));
      setDrafts((current) => ({
        ...current,
        [titleKey]: payload.title ?? '',
        [descriptionKey]: payload.description ?? ''
      }));
      panelSettings.refresh(state);
    } catch (error) {
      setDrafts((current) => ({
        ...current,
        [titleKey]: savedLabels?.[titleKey] ?? '',
        [descriptionKey]: savedLabels?.[descriptionKey] ?? ''
      }));
      toast({
        title: 'Не удалось сохранить название',
        description: describeError(error),
        variant: 'destructive'
      });
    } finally {
      setLabelPending((current) => ({ ...current, [titleKey]: false, [descriptionKey]: false }));
    }
  };

  const renderField = (panel, kind, defaultValue, ariaLabel) => {
    const key = panelFieldKey[kind](panel.id);

    return (
      <LabelField
        id={`panel-${kind}-${panel.id}`}
        trackId={kind === 'title' ? 'settings.interface.title_edit' : 'settings.interface.description_edit'}
        value={drafts[key] ?? ''}
        placeholder={defaultValue ?? '—'}
        pending={labelPending[key] === true}
        renamed={normalizeLabelInput(savedLabels?.[key]) !== null}
        ariaLabel={ariaLabel}
        onChange={(value) => setDraft(key, value)}
        onCommit={() => saveLabels(panel, { ...drafts, [key]: drafts[key] })}
        onRevert={() => setDraft(key, savedLabels?.[key] ?? '')}
        onReset={() => saveLabels(panel, { ...drafts, [key]: '' })}
      />
    );
  };

  const renderPanelRow = (panel) => {
    const titleKey = panelFieldKey.title(panel.id);
    const isRenamed = normalizeLabelInput(savedLabels?.[titleKey]) !== null;
    const isColumn = panel.group === 'column';

    return (
      <div key={panel.id} className="flex items-start gap-3 rounded-md border p-3">
        {/* Radix-чекбокс это button, поэтому подпись связываем через htmlFor, а не
            оборачиванием в <label>. */}
        {panel.renameOnly ? (
          <span className="mt-0.5 w-4 shrink-0" aria-hidden="true" />
        ) : (
          <Checkbox
            id={`panel-${panel.id}`}
            data-ev="settings.interface.panel_visibility_toggle"
            className="mt-0.5"
            checked={visibility?.[panel.id] === true}
            disabled={visibilityPending[panel.id] === true}
            onCheckedChange={(value) => handleToggle(panel, value === true)}
          />
        )}
        <div className="min-w-0 flex-1 space-y-2">
          <div className="space-y-1">
            <Label
              htmlFor={panel.renameOnly ? undefined : `panel-${panel.id}`}
              className={panel.renameOnly ? '' : 'cursor-pointer'}
            >
              {isRenamed ? savedLabels[titleKey] : panel.title}
            </Label>
            <div className="flex flex-wrap items-center gap-2">
              {panel.isTabLabel && (
                <Badge variant="secondary" className="font-normal">
                  раздел меню
                </Badge>
              )}
              {panel.group === 'kpi' && (
                <Badge variant="outline" className="font-normal">
                  плитка
                </Badge>
              )}
              {isColumn && (
                <Badge variant="outline" className="font-normal">
                  столбец
                </Badge>
              )}
              {panel.renameOnly && (
                <span className="text-xs text-muted-foreground">{hideHint(panel)}</span>
              )}
              {isRenamed && (
                <span className="text-xs text-muted-foreground">по умолчанию: {panel.title}</span>
              )}
            </div>
          </div>
          <div className="grid gap-1 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center sm:gap-3">
            <Label
              htmlFor={`panel-title-${panel.id}`}
              className="text-xs font-normal text-muted-foreground"
            >
              Название
            </Label>
            {renderField(
              panel,
              'title',
              panel.title,
              `${isColumn ? 'Название столбца' : 'Название панели'} ${panel.title}`
            )}
            {/* У раздела меню и у столбца таблицы подписи под названием нет — только оно само. */}
            {!panel.isTabLabel && !isColumn && (
              <>
                <Label
                  htmlFor={`panel-description-${panel.id}`}
                  className="text-xs font-normal text-muted-foreground"
                >
                  Подпись
                </Label>
                {renderField(panel, 'description', panel.description, `Подпись панели ${panel.title}`)}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (savedLabels === null || visibility === null) {
    return null;
  }

  return (
    <div className="space-y-6">
    <LayoutTabsCard />
    <Card>
      <CardHeader>
        <CardTitle>Названия и состав панелей</CardTitle>
        <CardDescription>
          Как называются панели разделов и какие из них показывать. Это касается только
          отображения: данные продолжают собираться, а выгрузки не меняются. Пустое поле
          возвращает значение по умолчанию. Настройка общая для всей инсталляции, а не
          персональная, и сохраняется сразу. Графики, которые строит пакет узлов анализа,
          настраиваются отдельно — на вкладке «Узлы анализа».
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {groups.map((group) => {
          const renamed = countRenamedPanels(group, savedLabels);
          const hidden = countHiddenPanels(group, visibility);

          return (
            <div key={group.id} className="space-y-3">
              <div className="flex flex-wrap items-baseline gap-2">
                <h4 className="text-sm font-semibold">{group.title}</h4>
                {renamed > 0 && (
                  <span className="text-xs text-muted-foreground">переименовано {renamed}</span>
                )}
                {hidden > 0 && <span className="text-xs text-muted-foreground">скрыто {hidden}</span>}
              </div>
              {group.tabLabel && (
                <div className="space-y-2">{renderPanelRow(group.tabLabel)}</div>
              )}
              {group.kpi.length > 0 && (
                <div className="space-y-2">{group.kpi.map(renderPanelRow)}</div>
              )}
              {group.cards.length > 0 && (
                <div className="space-y-2">{group.cards.map(renderPanelRow)}</div>
              )}
              {group.columns.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-medium text-muted-foreground">
                    Столбцы таблицы. Скрытый столбец убирает и одноимённый фильтр над таблицей,
                    если такой есть.
                  </div>
                  {group.columns.map(renderPanelRow)}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
    </div>
  );
};

export default InterfaceTab;
